import React, {useEffect, useState, useMemo, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {IClassroom, classroomInitial, reduxProps} from '../constants'
import { actionGetStudents, actionSaveSelectedClass } from "../redux/education";
import Select from './EducationSelect'
import {mapSelectOptions} from '../utils'
import { actionGetSubUserAvatars } from "../redux/user";
import { actionGetAvatars } from "../redux/content";
import {toast} from 'react-toastify';
import {classroomYearKey, classroomYearOptions, currentClassroomYear} from '../utils/classroomYear'
import './SelectClass.scss'
import {UiIcon} from './UiIcon'

export function SelectClass() {
  const dispatch = useDispatch()
  const {classroom, classrooms} = useSelector((state: reduxProps) => state.educationReducer)
  const currentYear = currentClassroomYear()
  const [selectedYear, setSelectedYear] = useState(String(currentYear))
  const yearOptions = useMemo(() => classroomYearOptions(classrooms, currentYear), [classrooms, currentYear])
  const assignedYears = useMemo(() => Array.from(new Set(classrooms.map(classroomYearKey))), [classrooms])
  const showYearSelector = assignedYears.length > 1
  const effectiveYear = assignedYears.length === 1 ? assignedYears[0] : selectedYear
  const visibleClassrooms = useMemo(() => classrooms.filter(item =>
    classroomYearKey(item) === effectiveYear), [classrooms, effectiveYear])
  const classroomDropdown = mapSelectOptions(visibleClassrooms, 'id', 'name')
  const selectedClass = visibleClassrooms.find(item => String(item.id) === String(classroom.id))

  const selectClassroom = useCallback((selected: IClassroom) => {
    dispatch(actionSaveSelectedClass(selected))
    if (!selected.id) return
    dispatch(actionGetStudents({classroomId: selected.id}, (result) => {
      if (result.type === 'SUCCESS') {
        const ids = result.payload.data.map((student: any) => student.subUserId)
        if (ids.length) dispatch(actionGetSubUserAvatars({ids: JSON.stringify(ids)}))
        dispatch(actionGetAvatars({}))
      }
    }))
  }, [dispatch])

  useEffect(() => {
    if (selectedClass) return
    const next = visibleClassrooms[0]
    if (next) selectClassroom(next)
    else if (classroom.id) selectClassroom({...classroomInitial, id: 0})
  }, [selectedClass, visibleClassrooms, classroom.id, selectClassroom])

  function dropdownChange(item: any) {
    const selected = visibleClassrooms.find(value => String(value.id) === String(item?.value))
    if (selected) selectClassroom(selected)
  }

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(classroom.classroomCode);
      toast.success('Sınıf kodu kopyalandı!', {
        autoClose: 2000,
        position: 'top-right',
      });
    } catch (err) {
      toast.error('Kopyalama işlemi başarısız!', {
        autoClose: 2000,
        position: 'top-right',
      });
    }
  };

  const handleShareQR = async () => {
    try {
      if (navigator.share) {
        // QR kod görselini de paylaş
        try {
          const response = await fetch(`kidly://app/get-student-code/${classroom.classroomCode}/qr.png`);
          const blob = await response.blob();
          const file = new File([blob], 'qr.png', { type: 'image/png' });

          await navigator.share({
            title: 'Sınıf QR Kodu',
            text: `Sınıf kodum: ${classroom.classroomCode}`,
            files: [file]
          });
        } catch (fileError) {
          // Dosya paylaşımı başarısız olursa sadece metin paylaş
          await navigator.share({
            title: 'Sınıf QR Kodu',
            text: `Sınıf kodum: ${classroom.classroomCode}`,
          });
        }
      } else {
        // Fallback: QR kod görselini indir
        const link = document.createElement('a');
        link.href = `https://production.gokidly.com/education/api/v3/classroom/${classroom.classroomCode}/qr.png`;
        link.download = 'qr-code.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('QR kod indirildi!', {
          autoClose: 2000,
          position: 'top-right',
        });
      }
    } catch (err) {
      toast.error('QR kod paylaşımı başarısız!', {
        autoClose: 2000,
        position: 'top-right',
      });
    }
  };

  return (
    <>
    <div className={'select-class'}>
      {showYearSelector && <>
      <label className="select-class-label" htmlFor="classroom-year">Eğitim öğretim yılı</label>
      <Select
        inputId="classroom-year"
        className="kidly-select class-year-select"
        classNamePrefix="kidly"
        options={yearOptions}
        value={yearOptions.find(option => option.value === selectedYear) || null}
        onChange={option => option && setSelectedYear(option.value)}
        isSearchable={false}
      />
      </>}
      <label htmlFor='sidebar-classroom' className='select-class-label'>Aktif sınıf</label>
      <Select
        className='kidly-select'
        classNamePrefix='kidly'
        inputId='sidebar-classroom'
        placeholder='Sınıf seçin'
        loadingMessage={() => 'Sınıflar yükleniyor…'}
        name='classroomId'
        noOptionsMessage={() => 'Bu eğitim yılında sınıfınız yok'}
        isDisabled={!visibleClassrooms.length}
        options={classroomDropdown}
        onChange={dropdownChange}
        value={classroomDropdown.find((item) => String(item.value) === String(selectedClass?.id)) || null}
      />
      {!visibleClassrooms.length && <p className="class-year-empty" role="status">Bu eğitim yılında atanmış sınıfınız yok. Diğer yılları kontrol edebilirsiniz.</p>}
      {selectedClass && <details className='class-invite' key={selectedClass.id}>
        <summary><UiIcon name='invite' size={16}/><span>Öğrenci davet et</span><UiIcon name='chevron-down' size={14}/></summary>
        <div className='class-share-tools'>
        <div className='class-code'>
          <span className='title'>Sınıf kodu</span>
          <span className='text'>{classroom.classroomCode}</span>
        </div>
        <div className='action-buttons'>
          <button type='button' disabled={!classroom.classroomCode} onClick={handleCopyCode} className='aside-action-btn'>
            <UiIcon name='copy' size={15}/>
            Kodu kopyala
          </button>
          <button type='button' disabled={!classroom.classroomCode} onClick={handleShareQR} className='aside-action-btn is-share'>
            <UiIcon name='share' size={15}/>
            Kodu paylaş
          </button>
        </div>
        </div>
      </details>}
    </div>
    </>
  )
}
